import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Filter, X, SlidersHorizontal } from 'lucide-react';
import { categories } from '../lib/categories';
import { cn } from '../lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
// Mock product data for the template
const mockProducts = [{
  sku: 'BST1SLC',
  name: 'Sleeveless Crew Neck No Button Baseball Jersey',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?file=uploaded%2Fimages%2FRSBSSL01+POLYDEX+FRONT.png&h=300&site=12118&user=6101&w=300',
  category: 'baseball',
  subcategory: 'jerseys'
}, {
  sku: 'BBJ2FB',
  name: 'Full Button Premium Baseball Jersey',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?file=uploaded%2Fimages%2FRSBSSL01+POLYDEX+FRONT.png&h=300&site=12118&user=6101&w=300',
  category: 'baseball',
  subcategory: 'jerseys'
}, {
  sku: 'BBP1KN',
  name: 'Pro-Weight Knicker Baseball Pants',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?file=uploaded%2Fimages%2FRSBSSL01+POLYDEX+FRONT.png&h=300&site=12118&user=6101&w=300',
  category: 'baseball',
  subcategory: 'pants'
}, {
  sku: 'BBCJ1SS',
  name: 'Short Sleeve Cage Jacket',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?file=uploaded%2Fimages%2FRSBSSL01+POLYDEX+FRONT.png&h=300&site=12118&user=6101&w=300',
  category: 'baseball',
  subcategory: 'cage-jackets'
}, {
  sku: 'BST2TB',
  name: 'Two-Button Performance Jersey',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?file=uploaded%2Fimages%2FRSBSSL01+POLYDEX+FRONT.png&h=300&site=12118&user=6101&w=300',
  category: 'baseball',
  subcategory: 'jerseys'
}, {
  sku: 'BBP2OB',
  name: 'Open Bottom Relaxed Fit Pants',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?file=uploaded%2Fimages%2FRSBSSL01+POLYDEX+FRONT.png&h=300&site=12118&user=6101&w=300',
  category: 'baseball',
  subcategory: 'pants'
}];
export const CategoryPage = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [activeSubcategory, setActiveSubcategory] = useState<string | null>(null);
  // Default to baseball if slug not found (for template purposes)
  const category = categories.find(c => c.slug === slug) || categories[0];
  return <div className="bg-white min-h-screen flex flex-col" data-id="element-382">
      {/* Breadcrumbs */}
      <div className="bg-gray50 border-b border-gray200 py-3" data-id="element-383">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-2 text-sm text-gray-500" data-id="element-384">
          <Link to="/" className="hover:text-charcoal transition-colors" data-id="element-385">
            Home
          </Link>
          <ChevronRight className="w-4 h-4" data-id="element-386" />
          <span className="text-charcoal font-semibold" data-id="element-387">{category.name}</span>
        </div>
      </div>

      {/* Category Hero */}
      <div className="bg-ink text-white py-12 relative overflow-hidden" data-id="element-388">
        {category.image && !category.iconOnly && <div className="absolute inset-0 opacity-20" data-id="element-389">
            <img src={category.image} alt="" className="w-full h-full object-cover" data-id="element-390" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-transparent" data-id="element-391"></div>
          </div>}
        <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-392">
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4" data-id="element-393">
            {category.name} Uniforms & Apparel
          </h1>
          <p className="text-gray-300 max-w-2xl text-lg" data-id="element-394">
            Wholesale custom {category.name.toLowerCase()} uniforms built for
            dealers, resellers, and team stores. Start your design in 3D or
            apply for wholesale access to view pricing.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 flex-grow w-full flex flex-col md:flex-row gap-8" data-id="element-395">
        {/* Mobile Filter Toggle */}
        <div className="md:hidden flex items-center justify-between mb-4" data-id="element-396">
          <button onClick={() => setIsMobileFilterOpen(true)} className="flex items-center gap-2 text-charcoal font-semibold border border-gray200 px-4 py-2 rounded-sm" data-id="element-397">
            <SlidersHorizontal className="w-4 h-4" data-id="element-398" /> Filter & Categories
          </button>
          <span className="text-sm text-gray-500" data-id="element-399">
            {mockProducts.length} Products
          </span>
        </div>

        {/* Sidebar (Desktop) */}
        <aside className="hidden md:block w-64 shrink-0" data-id="element-400">
          <div className="sticky top-24" data-id="element-401">
            <h3 className="font-heading font-bold text-lg mb-4 border-b border-gray200 pb-2" data-id="element-402">
              Categories
            </h3>
            <ul className="flex flex-col gap-1" data-id="element-403">
              <li data-id="element-404">
                <button onClick={() => setActiveSubcategory(null)} className={cn('w-full text-left px-3 py-2 rounded-sm text-sm font-medium transition-colors', activeSubcategory === null ? 'bg-gray100 text-brandRed' : 'text-gray-600 hover:bg-gray50 hover:text-charcoal')} data-id="element-405">
                  All {category.name}
                </button>
              </li>
              {category.subcategories.map(sub => <li key={sub.slug} data-id="element-406">
                  <button onClick={() => setActiveSubcategory(sub.slug)} className={cn('w-full text-left px-3 py-2 rounded-sm text-sm font-medium transition-colors', activeSubcategory === sub.slug ? 'bg-gray100 text-brandRed' : 'text-gray-600 hover:bg-gray50 hover:text-charcoal')} data-id="element-407">
                    {sub.name}
                  </button>
                  {sub.children && activeSubcategory === sub.slug && <ul className="pl-6 mt-1 flex flex-col gap-1" data-id="element-408">
                      {sub.children.map(child => <li key={child.slug} data-id="element-409">
                          <button className="w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:text-charcoal transition-colors" data-id="element-410">
                            {child.name}
                          </button>
                        </li>)}
                    </ul>}
                </li>)}
            </ul>

            <div className="mt-8 bg-gray50 p-4 rounded-lg border border-gray200" data-id="element-411">
              <h4 className="font-bold text-sm mb-2" data-id="element-412">Wholesale Pricing</h4>
              <p className="text-xs text-gray-600 mb-4" data-id="element-413">
                Log in to your dealer account to view wholesale pricing and
                combined quantity discounts.
              </p>
              <Link to="/wholesale-apply" className="text-xs font-bold text-brandRed hover:text-brandRedDark flex items-center gap-1" data-id="element-414">
                Apply for Access <ChevronRight className="w-3 h-3" data-id="element-415" />
              </Link>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-grow" data-id="element-416">
          {/* Subcategory Chips (Desktop) */}
          <div className="hidden md:flex flex-wrap gap-2 mb-8" data-id="element-417">
            {category.subcategories.map(sub => <button key={sub.slug} onClick={() => setActiveSubcategory(sub.slug)} className={cn('px-4 py-1.5 rounded-full text-sm font-medium border transition-colors', activeSubcategory === sub.slug ? 'bg-charcoal border-charcoal text-white' : 'bg-white border-gray200 text-gray-600 hover:border-charcoal hover:text-charcoal')} data-id="element-418">
                {sub.name}
              </button>)}
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-419">
            {mockProducts.map(product => <Link key={product.sku} to={`/product/${product.sku}`} className="group flex flex-col bg-white border border-gray200 rounded-lg overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all hover:-translate-y-1" data-id="element-420">
                <div className="aspect-square bg-gray50 p-6 relative" data-id="element-421">
                  <img src={product.image} alt={product.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" data-id="element-422" />
                  <div className="absolute top-3 left-3 bg-white px-2 py-1 text-[10px] font-mono font-bold text-gray-500 rounded border border-gray200" data-id="element-423">
                    {product.sku}
                  </div>
                </div>
                <div className="p-4 flex flex-col flex-grow" data-id="element-424">
                  <h3 className="font-bold text-charcoal leading-tight mb-2 group-hover:text-brandRed transition-colors" data-id="element-425">
                    {product.name}
                  </h3>
                  <div className="mt-auto pt-4 flex items-center justify-between" data-id="element-426">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-427">
                      Wholesale Only
                    </span>
                    <span className="text-sm font-bold text-brandRed flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-428">
                      View Details <ChevronRight className="w-4 h-4" data-id="element-429" />
                    </span>
                  </div>
                </div>
              </Link>)}
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      <AnimatePresence data-id="element-430">
        {isMobileFilterOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsMobileFilterOpen(false)} className="fixed inset-0 bg-ink/50 z-50 md:hidden" data-id="element-431" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'tween',
          duration: 0.3
        }} className="fixed inset-y-0 left-0 w-4/5 max-w-sm bg-white z-50 shadow-2xl flex flex-col md:hidden" data-id="element-432">
              <div className="p-4 border-b border-gray200 flex items-center justify-between bg-gray50" data-id="element-433">
                <span className="font-heading font-bold text-lg" data-id="element-434">
                  Filter & Categories
                </span>
                <button onClick={() => setIsMobileFilterOpen(false)} className="p-2 text-gray-500 hover:text-charcoal" data-id="element-435">
                  <X className="w-5 h-5" data-id="element-436" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-grow" data-id="element-437">
                <ul className="flex flex-col gap-2" data-id="element-438">
                  <li data-id="element-439">
                    <button onClick={() => {
                  setActiveSubcategory(null);
                  setIsMobileFilterOpen(false);
                }} className={cn('w-full text-left px-4 py-3 rounded-sm font-medium transition-colors', activeSubcategory === null ? 'bg-gray100 text-brandRed' : 'text-gray-600')} data-id="element-440">
                      All {category.name}
                    </button>
                  </li>
                  {category.subcategories.map(sub => <li key={sub.slug} data-id="element-441">
                      <button onClick={() => {
                  setActiveSubcategory(sub.slug);
                  setIsMobileFilterOpen(false);
                }} className={cn('w-full text-left px-4 py-3 rounded-sm font-medium transition-colors', activeSubcategory === sub.slug ? 'bg-gray100 text-brandRed' : 'text-gray-600')} data-id="element-442">
                        {sub.name}
                      </button>
                    </li>)}
                </ul>
              </div>
              <div className="p-4 border-t border-gray200 bg-gray50" data-id="element-443">
                <Link to="/wholesale-apply" className="block w-full bg-charcoal text-white text-center font-bold py-3 rounded-sm" data-id="element-444">
                  Apply for Wholesale Access
                </Link>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Bottom CTA Banner */}
      <div className="bg-gray100 border-t border-gray200 py-12 mt-auto" data-id="element-445">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-446">
          <h2 className="text-2xl font-heading font-bold text-ink mb-6" data-id="element-447">
            Ready to build your {category.name.toLowerCase()} uniforms?
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-448">
            <Link to="/products" className="w-full sm:w-auto bg-brandRed hover:bg-brandRedDark text-white font-bold py-3.5 px-8 rounded-sm transition-colors" data-id="element-449">
              Start Your Design in 3D
            </Link>
            <Link to="/wholesale-apply" className="w-full sm:w-auto bg-white border-2 border-charcoal hover:bg-charcoal hover:text-white text-charcoal font-bold py-3 px-8 rounded-sm transition-colors" data-id="element-450">
              Apply for Wholesale Access
            </Link>
          </div>
        </div>
      </div>
    </div>;
};